/**
 * Idle-activity detector — fires `onTimeout` after `timeoutMs` of no
 * input events. Watches a fixed set of user-input events; the list is
 * exported so wiring tests can pin it.
 *
 * Visibility changes do NOT reset the timer — idle is idle, regardless
 * of whether the tab is foregrounded. Network activity, query refetches,
 * and route changes also do NOT reset the timer; only direct user input
 * counts as "active."
 *
 * Pure module — no React dependency. The React wiring lives in
 * `useIdleTimeout`. Tests exercise the detector with a fake target.
 */

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000

export const IDLE_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const

/**
 * Minimal `EventTarget` subset the detector uses — typed locally so
 * tests can inject a fake without dragging in DOM globals.
 */
export interface IdleEventTarget {
  addEventListener(
    type: string,
    listener: () => void,
    options?: { passive?: boolean },
  ): void
  removeEventListener(type: string, listener: () => void): void
}

export interface IdleDetectorOptions {
  /** Idle window in milliseconds before `onTimeout` fires. */
  timeoutMs: number
  /**
   * Fires once when the window elapses without activity. After firing
   * the detector is in the "stopped" state; call `start()` to re-arm it
   * (e.g. after re-authentication).
   */
  onTimeout: () => void
  /** Defaults to `window`; injectable for tests. */
  target?: IdleEventTarget
}

export interface IdleDetector {
  /** Arms the detector and starts the inactivity countdown. Idempotent. */
  start: () => void
  /** Disarms the detector and removes all listeners. Idempotent. */
  stop: () => void
}

export function createIdleDetector(opts: IdleDetectorOptions): IdleDetector {
  const target = opts.target ?? (globalThis as unknown as { window: IdleEventTarget }).window
  let timerId: ReturnType<typeof setTimeout> | null = null
  let started = false

  const fire = (): void => {
    started = false
    timerId = null
    for (const event of IDLE_EVENTS) target.removeEventListener(event, onActivity)
    opts.onTimeout()
  }

  const onActivity = (): void => {
    if (!started) return
    if (timerId !== null) clearTimeout(timerId)
    timerId = setTimeout(fire, opts.timeoutMs)
  }

  return {
    start(): void {
      if (started) return
      started = true
      for (const event of IDLE_EVENTS) {
        target.addEventListener(event, onActivity, { passive: true })
      }
      timerId = setTimeout(fire, opts.timeoutMs)
    },
    stop(): void {
      if (!started && timerId === null) return
      started = false
      for (const event of IDLE_EVENTS) target.removeEventListener(event, onActivity)
      if (timerId !== null) {
        clearTimeout(timerId)
        timerId = null
      }
    },
  }
}
